// src/pages/ParcoMezziPage.js
// Anagrafica dei mezzi e delle attrezzature che vale la pena tracciare singolarmente — non
// tutto il materiale minuto, solo ciò che ha senso sapere sempre "dove sta, chi ce l'ha".
// Gestita di default solo da titolare/admin, ma abilitabile a persone specifiche (stesso
// principio già usato per Note Spese e Cartellini).
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, addDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { useCanSee } from "../hooks/useCanSee";
import { Plus, Trash2, X } from "lucide-react";

const TIPI = { furgone: { label:"Furgone", emoji:"🚐" }, auto: { label:"Auto", emoji:"🚗" }, attrezzatura: { label:"Attrezzatura", emoji:"🛠️" } };
const FORM_VUOTO = { tipo:"furgone", nome:"", targa:"", note:"" };

export default function ParcoMezziPage({ onNavigate }) {
  const { isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const canSee = useCanSee();
  const { addToast } = useToast();
  const canManage = isOrgAdmin || isPlatformOwner || canSee("gestione-parco-mezzi");

  const [voci, setVoci] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(FORM_VUOTO);

  useEffect(() => {
    if (!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"mezziAttrezzature"), where("orgId","==",effectiveOrgId)),
      snap => setVoci(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>a.tipo.localeCompare(b.tipo)||a.nome.localeCompare(b.nome))));
  }, [effectiveOrgId]);

  const salva = async () => {
    if (!form.nome.trim()) { addToast("Il nome è obbligatorio","error"); return; }
    await addDoc(collection(db,"mezziAttrezzature"), {
      orgId: effectiveOrgId, tipo: form.tipo, nome: form.nome.trim(), targa: form.targa.trim(), note: form.note.trim(),
      possessoreUid: null, possessoreNome: null, possessoreDal: null,
      createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
    addToast("Aggiunto ✅","success");
    setForm(FORM_VUOTO); setShowForm(false);
  };

  const elimina = async (v) => {
    if (!(await confirm(`Eliminare "${v.nome}" dal parco mezzi?`))) return;
    await deleteDoc(doc(db,"mezziAttrezzature",v.id));
    addToast("Eliminato","success");
  };

  if (!canManage) return <div style={{padding:40,textAlign:"center",color:"#9ca3af"}}>Non hai i permessi per gestire questa sezione.</div>;

  return (
    <div style={{padding:24,maxWidth:800}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:18}}>
        <div>
          <h2 style={{fontSize:18,fontWeight:800,color:"#1e1b4b",marginBottom:2}}>🚐 Parco Mezzi e Attrezzature</h2>
          <p style={{fontSize:12,color:"#6b7280"}}>I mezzi e le attrezzature qui registrati compaiono in "Disponibilità Mezzi e Attrezzature", nell'area personale di tutti.</p>
        </div>
        <button onClick={()=>setShowForm(true)} style={{display:"flex",alignItems:"center",gap:5,padding:"7px 14px",borderRadius:8,border:"none",background:"#7c3aed",color:"#fff",fontSize:12.5,fontWeight:700,cursor:"pointer",flexShrink:0}}>
          <Plus size={14}/> Nuovo
        </button>
      </div>

      {showForm && (
        <div style={{background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:12,padding:16,marginBottom:18}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
            <span style={{fontSize:13,fontWeight:700}}>Nuovo mezzo/attrezzatura</span>
            <button onClick={()=>{setShowForm(false);setForm(FORM_VUOTO);}} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af"}}><X size={16}/></button>
          </div>
          <div style={{display:"flex",gap:8,marginBottom:10}}>
            {Object.entries(TIPI).map(([k,t]) => (
              <button key={k} type="button" onClick={()=>setForm(f=>({...f,tipo:k}))}
                style={{flex:1,padding:"8px",borderRadius:8,border:`1px solid ${form.tipo===k?"#7c3aed":"#e5e7eb"}`,background:form.tipo===k?"#f5f3ff":"#fff",color:form.tipo===k?"#7c3aed":"#6b7280",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                {t.emoji} {t.label}
              </button>
            ))}
          </div>
          <input value={form.nome} onChange={e=>setForm(f=>({...f,nome:e.target.value}))} placeholder="Nome (es. Ducato bianco, Trapano Bosch)"
            style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12.5,marginBottom:8,boxSizing:"border-box"}}/>
          {form.tipo!=="attrezzatura" && (
            <input value={form.targa} onChange={e=>setForm(f=>({...f,targa:e.target.value}))} placeholder="Targa"
              style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12.5,marginBottom:8,boxSizing:"border-box"}}/>
          )}
          <input value={form.note} onChange={e=>setForm(f=>({...f,note:e.target.value}))} placeholder="Note (facoltative)"
            style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12.5,marginBottom:10,boxSizing:"border-box"}}/>
          <button onClick={salva} style={{padding:"8px 20px",borderRadius:8,border:"none",background:"#7c3aed",color:"#fff",fontSize:12.5,fontWeight:700,cursor:"pointer"}}>Salva</button>
        </div>
      )}

      {voci.length===0 ? (
        <div style={{textAlign:"center",padding:40,color:"#9ca3af",fontSize:12.5}}>Nessun mezzo o attrezzatura registrato ancora.</div>
      ) : voci.map(v => {
        const t = TIPI[v.tipo];
        return (
          <div key={v.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,marginBottom:6}}>
            <span style={{fontSize:18}}>{t.emoji}</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:700,color:"#111827"}}>{v.nome} {v.targa?<span style={{color:"#9ca3af",fontWeight:500}}>· {v.targa}</span>:""}</div>
              <div style={{fontSize:11,color:"#6b7280"}}>
                {v.possessoreNome ? `In uso a ${v.possessoreNome}` : "Disponibile"}
                {v.note?` · ${v.note}`:""}
              </div>
            </div>
            <button onClick={()=>elimina(v)} style={{background:"none",border:"none",cursor:"pointer",color:"#dc2626",padding:4}}><Trash2 size={14}/></button>
          </div>
        );
      })}
    </div>
  );
}
